import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { ExternalLink, Mail, CloudUpload } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useMatter } from '../hooks/useMatter';
import ClientVisasPanel from '../components/ClientVisasPanel';
import MatterDependentsPanel from '../components/MatterDependentsPanel';
import DeadlinesList from '../components/DeadlinesList';
import SalesBackgroundPanel, { type SalesBackground } from '../components/SalesBackgroundPanel';
import MatterReadiness from '../components/MatterReadiness';
import LodgementGate from '../components/LodgementGate';
import { ChangeRelationshipBasis } from '../components/ChangeRelationshipBasis';
import AssignStaffEdit from '../components/AssignStaffEdit';
import SponsorPanel from '../components/SponsorPanel';
import LifecycleTimeline from '../components/LifecycleTimeline';
import PaymentReceipts from '../components/PaymentReceipts';
import { useMatterTimeline } from '../lib/useMatterTimeline';
import StaffPdfActions from '../components/StaffPdfActions';
import EngagementLetterPanel from '../components/EngagementLetterPanel';
import { fetchDeadlines, type Deadline } from '../lib/deadlines';
import { sponsorRoleLabel } from '../lib/schema-runtime';
import type { EngagementLetterState } from '../lib/engagementLetter';

interface MatterDetail {
  id: string;
  matter_id: string | null;
  visa_subclass: string;
  variant: string | null;
  relationship_status: string | null;
  status: string;
  lodgement_date: string | null;
  rfi_deadline: string | null;
  initial_payment_date: string | null;
  final_payment_date: string | null;
  trn: string | null;
  drive_folder_url: string | null;
  ghl_opportunity_id: string | null;
  engagement_letter: EngagementLetterState | null;
  primary_contact: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
  sponsor_contact: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
  assigned_staff: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
  reviewer_staff?: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
}

interface AuditEntry {
  id: number;
  action: string;
  table_name: string | null;
  occurred_at: string;
  user_id: string | null;
  row_id: string | null;
  changes: Record<string, unknown> | null;
}

export default function MatterOverview() {
  const { matter } = useOutletContext<{ matter: MatterDetail }>();
  const { actuallyStaff } = useMatter();
  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  const [deadlinesLoading, setDeadlinesLoading] = useState(true);
  const [recent, setRecent] = useState<AuditEntry[]>([]);
  const [loadingExtras, setLoadingExtras] = useState(true);
  const [salesBackground, setSalesBackground] = useState<SalesBackground | null>(null);
  const [ghlContactId, setGhlContactId] = useState<string | null>(null);
  const [salesBackgroundReloadKey, setSalesBackgroundReloadKey] = useState(0);
  // Staff-only (B13/S2 fix): the note captured on the Create Matter form.
  // Fetched only for staff sessions — internal_notes has no RLS column
  // restriction (RLS is row-level only), so this is kept out of any
  // client-reachable query on principle; see the migration comment.
  const [internalNotes, setInternalNotes] = useState<string | null>(null);
  // Bumped when staff record/upload the engagement letter, so LodgementGate
  // re-evaluates its lodgement gate without a full page reload.
  const [engagementReloadKey, setEngagementReloadKey] = useState(0);
  // Lookups so describeAudit can render specific names instead of UUIDs.
  // contactNames keys on contacts.id, userToName keys on auth user_id (the
  // value stored in audit_log.user_id).
  const [contactNames, setContactNames] = useState<Record<string, string>>({});
  const [userToName, setUserToName] = useState<Record<string, string>>({});
  // documents.id -> filename, so describeAudit can render "Document approved
  // — passport.pdf" for document.* events that only carry the row_id.
  const [documentFilenames, setDocumentFilenames] = useState<Record<string, string>>({});

  // Pull sales_background from matters (not in the parent fetch). Re-runs when
  // the user clicks "Re-pull" via the panel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Types are still stale wrt the 20260430000001 migration; cast the row
      // until database.types.ts is regenerated.
      const { data } = await supabase
        .from('matters')
        .select('sales_background, ghl_contact_id')
        .eq('id', matter.id)
        .maybeSingle();
      const row = data as unknown as { sales_background: SalesBackground | null; ghl_contact_id: string | null } | null;
      if (cancelled || !row) return;
      setSalesBackground(row.sales_background ?? null);
      setGhlContactId(row.ghl_contact_id ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [matter.id, salesBackgroundReloadKey]);

  // Pull internal_notes for staff only (B13/S2 fix) — not part of the
  // client-reachable MatterDetailLayout query above, and this effect itself
  // never fires for a client session, so the value never reaches a client
  // browser's network response either.
  useEffect(() => {
    if (!actuallyStaff) {
      setInternalNotes(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('matters')
        .select('internal_notes')
        .eq('id', matter.id)
        .maybeSingle();
      if (cancelled) return;
      setInternalNotes(data?.internal_notes ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [matter.id, actuallyStaff]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingExtras(true);
      try {
        // Two sources: events keyed straight to the matter row, and the custom
        // document.* events, which log the DOCUMENT id in row_id (see
        // describeAuditRow below) and so can only be found via the matter's
        // document ids.
        //
        // This previously filtered on `meta->>matter_id`. There is no `meta`
        // column on audit_log and there never has been (absent from the initial
        // migration and from database.types.ts), so that query 400'd on every
        // matter open and every document event was silently missing from this
        // panel. PostgREST filter strings are not type-checked, which is why it
        // compiled.
        //
        // Pull a wider window — was 5 (split across 10+10), now 30 — so the
        // panel doubles as a useful audit log not just a teaser. Sorted by
        // most recent first.
        const { data: docRows } = await supabase
          .from('documents')
          .select('id')
          .eq('matter_id', matter.id);
        if (cancelled) return;
        const matterDocumentIds = (docRows ?? []).map((d) => d.id);

        const [byRow, byDoc] = await Promise.all([
          supabase
            .from('audit_log')
            .select('id, action, table_name, occurred_at, user_id, row_id, changes')
            .eq('row_id', matter.id)
            .order('occurred_at', { ascending: false })
            .limit(50),
          matterDocumentIds.length
            ? supabase
                .from('audit_log')
                .select('id, action, table_name, occurred_at, user_id, row_id, changes')
                .in('row_id', matterDocumentIds)
                .order('occurred_at', { ascending: false })
                .limit(50)
            : Promise.resolve({ data: [] as AuditEntry[] }),
        ]);
        if (cancelled) return;
        const merged = new Map<number, AuditEntry>();
        for (const row of (byRow.data as AuditEntry[]) ?? []) merged.set(row.id, row);
        for (const row of (byDoc.data as AuditEntry[]) ?? []) merged.set(row.id, row);
        const sorted = Array.from(merged.values())
          .sort((a, b) => (a.occurred_at < b.occurred_at ? 1 : -1))
          .slice(0, 30);
        setRecent(sorted);

        // Collect every contact_id referenced in the change payloads + every
        // auth user_id that appears as an actor. One round-trip resolves
        // both groups (contact_id direct, user_id → contact via user_id col).
        const contactIds = new Set<string>();
        const userIds = new Set<string>();
        // Document ids referenced by document.* logAuditEvent calls or by
        // generic insert/update audit rows on the documents / document_comments
        // tables. Used to resolve filenames so we can render
        // "Document approved — passport.pdf by Tzvi Balbin".
        const documentIds = new Set<string>();
        for (const r of sorted) {
          if (r.user_id) userIds.add(r.user_id);
          const c = (r.changes ?? {}) as Record<string, unknown>;
          const after = c.after as Record<string, unknown> | undefined;
          const before = c.before as Record<string, unknown> | undefined;
          for (const obj of [after, before, r.changes as Record<string, unknown>]) {
            if (!obj) continue;
            for (const key of ['assigned_staff_id', 'reviewer_staff_id', 'reviewed_by', 'author_contact_id', 'contact_id', 'primary_contact_id']) {
              const v = obj[key];
              if (typeof v === 'string') contactIds.add(v);
            }
          }
          // Custom document.* events log the document id as audit_log.row_id
          // and use tableName='documents'. The trigger-driven insert/update
          // events on the documents table do the same. Generic inserts on the
          // document_comments table reference the document via
          // changes.document_id.
          if (r.action.startsWith('document.') && typeof r.row_id === 'string') {
            documentIds.add(r.row_id);
          }
          if (r.table_name === 'documents' && typeof r.row_id === 'string') {
            documentIds.add(r.row_id);
          }
          if (r.table_name === 'document_comments') {
            for (const obj of [after, before, c]) {
              if (!obj) continue;
              const d = obj.document_id;
              if (typeof d === 'string') documentIds.add(d);
            }
          }
        }
        // Fan out the filename lookup in parallel with the contact lookup.
        if (documentIds.size > 0) {
          supabase
            .from('documents')
            .select('id, filename')
            .in('id', Array.from(documentIds))
            .then(({ data: docs }) => {
              if (cancelled || !docs) return;
              const map: Record<string, string> = {};
              for (const d of docs as Array<{ id: string; filename: string | null }>) {
                if (d.filename) map[d.id] = d.filename;
              }
              setDocumentFilenames(map);
            });
        }
        if (contactIds.size > 0 || userIds.size > 0) {
          const orParts: string[] = [];
          if (contactIds.size > 0) orParts.push(`id.in.(${Array.from(contactIds).join(',')})`);
          if (userIds.size > 0) orParts.push(`user_id.in.(${Array.from(userIds).join(',')})`);
          const { data: cs } = await supabase
            .from('contacts')
            .select('id, user_id, given_names, family_name')
            .or(orParts.join(','));
          if (!cancelled && cs) {
            const byContact: Record<string, string> = {};
            const byUser: Record<string, string> = {};
            for (const c of cs as Array<{ id: string; user_id: string | null; given_names: string | null; family_name: string | null }>) {
              const name = [c.given_names, c.family_name].filter(Boolean).join(' ').trim();
              if (!name) continue;
              byContact[c.id] = name;
              if (c.user_id) byUser[c.user_id] = name;
            }
            setContactNames(byContact);
            setUserToName(byUser);
          }
        }
      } catch {
        // best-effort: overview keeps rendering even if audit query fails
      } finally {
        if (!cancelled) setLoadingExtras(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [matter.id]);

  // Per-matter deadlines (visa expiry for applicant + lodgement target + RFI for this matter).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDeadlinesLoading(true);
      try {
        const list = await fetchDeadlines({ matterId: matter.id });
        if (!cancelled) setDeadlines(list);
      } catch {
        // ignore — Overview keeps rendering
      } finally {
        if (!cancelled) setDeadlinesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [matter.id]);

  // Pull GHL "missing fields" hints from sales_background. The deal-Won
  // webhook stamps these when it had to log a partial creation.
  const ghlMissingFields = useMemo<string[] | null>(() => {
    if (!salesBackground) return null;
    const missing = (salesBackground as unknown as { missing_fields?: unknown }).missing_fields;
    return Array.isArray(missing) ? (missing as string[]) : null;
  }, [salesBackground]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      {/* MAIN COLUMN */}
      <div className="space-y-5">
        {/* Readiness chip + Quick Fix. Staff-only; clients don't need to see this. */}
        {actuallyStaff && matter.primary_contact && (
          <MatterReadiness
            matterId={matter.id}
            visaSubclass={matter.visa_subclass}
            applicantContactId={matter.primary_contact.id}
            sponsorContactId={matter.sponsor_contact?.id ?? null}
            ghlMissingFields={ghlMissingFields}
            reloadKey={salesBackgroundReloadKey}
          />
        )}

        {/* Staff: switch a partner matter between de facto and married when the
            couple's circumstances change mid-application. */}
        {actuallyStaff && matter.primary_contact && (
          <ChangeRelationshipBasis
            matterId={matter.id}
            visaSubclass={matter.visa_subclass}
            variant={matter.variant}
            relationshipStatus={matter.relationship_status}
            applicantContactId={matter.primary_contact.id}
          />
        )}

        {/* Lodgement gate — only meaningful for staff. Combines docs-approved
            + intake-forms-completed into a single readiness panel. CTA appears
            when every gate is green. */}
        {actuallyStaff && matter.primary_contact && (
          <LodgementGate
            matterId={matter.id}
            visaSubclass={matter.visa_subclass}
            variant={matter.variant}
            applicantContactId={matter.primary_contact.id}
            sponsorContactId={matter.sponsor_contact?.id ?? null}
            matterStatus={matter.status}
            initialPaymentDate={matter.initial_payment_date}
            finalPaymentDate={matter.final_payment_date}
            reloadKey={engagementReloadKey}
          />
        )}

        {/* Client-facing unified timeline. Same context-driven steps as the
            staff LodgementGate, just rendered with friendlier labels. */}
        {!actuallyStaff && matter.primary_contact && (
          <ClientLifecycleCard
            matterId={matter.id}
            visaSubclass={matter.visa_subclass}
            variant={matter.variant}
            applicantContactId={matter.primary_contact.id}
            sponsorContactId={matter.sponsor_contact?.id ?? null}
            matter={{
              status: matter.status,
              initial_payment_date: matter.initial_payment_date,
              final_payment_date: matter.final_payment_date,
            }}
          />
        )}

        {/* Payment receipts — staff-only collapsible panel.
            Sits between the lodgement gate (with its initial/final payment
            dates) and the staff PDF actions / sales background. Default
            collapsed; AI extraction has hard cost caps inside the component. */}
        {actuallyStaff && (
          <PaymentReceipts matterId={matter.id} />
        )}

        {/* Working-copy PDF download — staff transcription helper. */}
        {actuallyStaff && matter.primary_contact && (
          <StaffPdfActions
            matterId={matter.id}
            matterCode={matter.matter_id}
            visaSubclass={matter.visa_subclass}
            variant={matter.variant}
            applicantContactId={matter.primary_contact.id}
            sponsorContactId={matter.sponsor_contact?.id ?? null}
          />
        )}

        {/* Background from sales (staff-only). Hidden when null (no GHL pull yet). */}
        {actuallyStaff && (
          <SalesBackgroundPanel
            matterId={matter.id}
            primaryContactEmail={matter.primary_contact?.email ?? null}
            ghlContactId={ghlContactId}
            background={salesBackground}
            onRepulled={() => setSalesBackgroundReloadKey((k) => k + 1)}
            canRepull={!!actuallyStaff}
          />
        )}

        {/* Key facts (trimmed: matter ID + visa moved to header) */}
        <Section title="Key facts">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Fact label="MARA agent" value="Eli Bekker · MARN 1465541" />
            <Fact
              label="Initial payment"
              value={
                matter.initial_payment_date
                  ? `Received ${formatDate(matter.initial_payment_date)}`
                  : 'Not yet received'
              }
              tone={matter.initial_payment_date ? 'good' : 'warn'}
            />
            <Fact
              label="Final payment"
              value={
                matter.final_payment_date
                  ? `Received ${formatDate(matter.final_payment_date)}`
                  : 'Pending (sent with first draft)'
              }
              tone={matter.final_payment_date ? 'good' : undefined}
            />
            {matter.trn && <Fact label="TRN" value={matter.trn} mono />}
          </dl>
        </Section>

        {/* Internal notes — staff-only, read-only (B13/S2 fix). Captured once
            on the Create Matter form; not a full notes thread yet. */}
        {actuallyStaff && internalNotes && (
          <Section title="Internal notes">
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {internalNotes}
            </p>
          </Section>
        )}

        {/* Per-matter deadlines */}
        <Section title="Deadlines">
          {deadlinesLoading ? (
            <div className="text-sm text-slate-500">Loading deadlines...</div>
          ) : (
            <DeadlinesList
              items={deadlines}
              compact
              emptyMessage="No deadlines on this matter in the next 30 days."
            />
          )}
        </Section>

        {/* Recent activity */}
        <Section title="Recent activity">
          {loadingExtras ? (
            <div className="text-sm text-slate-500">Loading...</div>
          ) : recent.length === 0 ? (
            <div className="text-sm text-slate-500">No activity recorded yet.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {recent.map((e) => {
                const desc = describeAudit(e, contactNames, documentFilenames);
                const actor = e.user_id ? userToName[e.user_id] : null;
                return (
                  <li key={e.id} className="leading-relaxed">
                    <span className="font-medium text-[#0B2341]">{desc.title}</span>
                    {desc.detail && <span className="text-slate-600"> - {desc.detail}</span>}
                    {actor && <span className="text-slate-500"> by {actor}</span>}
                    <span className="block text-[11px] text-slate-400">
                      {formatFullDateTime(e.occurred_at)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>
      </div>

      {/* RIGHT COLUMN — titles live inside cards to reduce visual fragmentation */}
      <div className="space-y-4">
        <RailCard title="People">
          <PersonRow label="Applicant" person={matter.primary_contact} />
          {actuallyStaff ? (
            <SponsorPanel
              matterId={matter.id}
              visaSubclass={matter.visa_subclass}
              sponsor={matter.sponsor_contact}
              onSaved={() => window.location.reload()}
            />
          ) : (
            matter.sponsor_contact && (
              <PersonRow
                label={sponsorRoleLabel(matter.visa_subclass)}
                person={matter.sponsor_contact}
              />
            )
          )}
          <PersonRow
            label="Assigned to"
            person={matter.assigned_staff}
            edit={actuallyStaff ? (
              <AssignStaffEdit
                matterId={matter.id}
                column="assigned_staff_id"
                currentStaffId={matter.assigned_staff?.id ?? null}
                onSaved={() => window.location.reload()}
              />
            ) : null}
          />
          {matter.reviewer_staff && (
            <PersonRow
              label="Reviewer"
              person={matter.reviewer_staff}
              edit={actuallyStaff ? (
                <AssignStaffEdit
                  matterId={matter.id}
                  column="reviewer_staff_id"
                  currentStaffId={matter.reviewer_staff?.id ?? null}
                  onSaved={() => window.location.reload()}
                />
              ) : null}
            />
          )}
        </RailCard>

        {matter.primary_contact && (
          <ClientVisasPanel
            contactId={matter.primary_contact.id}
            isStaff={actuallyStaff}
            currentMatterId={matter.id}
          />
        )}

        <MatterDependentsPanel matterId={matter.id} isStaff={actuallyStaff} />

        <RailCard title="External links">
          <div className="space-y-2 text-sm">
            {matter.drive_folder_url ? (
              <a
                href={matter.drive_folder_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2CABEA] hover:underline inline-flex items-center gap-1"
              >
                <ExternalLink size={12} /> Drive folder
              </a>
            ) : (
              <div className="text-slate-400 text-xs">No Drive folder linked</div>
            )}
            {matter.ghl_opportunity_id ? (
              <div className="text-slate-500 text-xs">
                GHL opportunity: <span className="font-mono">{matter.ghl_opportunity_id}</span>
              </div>
            ) : (
              <div className="text-slate-400 text-xs">Not synced to GHL</div>
            )}
          </div>
        </RailCard>

        {/* Drive sync — staff-only (A.10). Manually mirror every document on
            this matter to the ADI shared drive. Best-effort; enqueues a retry
            row per doc. Sits below External links, clear of the engagement
            letter panel and compliance rail. */}
        {actuallyStaff && <DriveSyncCard matterId={matter.id} />}

        <EngagementLetterPanel
          matterId={matter.id}
          isStaff={!!actuallyStaff}
          onChange={() => setEngagementReloadKey((k) => k + 1)}
        />

        <RailCard title="Compliance">
          <ul className="space-y-1.5 text-sm">
            <li className="text-slate-700">MARA agent on file: Eli Bekker (MARN 1465541)</li>
          </ul>
        </RailCard>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">{title}</h2>
      <div className="card p-4">{children}</div>
    </div>
  );
}

function RailCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-4 pt-3 pb-2 border-b border-slate-100">
        <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

/**
 * Staff-only card with a "Sync this matter to Drive" button (A.10). Calls the
 * staff-only enqueue_matter_drive_sync RPC, which queues every active document
 * on the matter for the off-portal Drive mirror. Best-effort: the cron worker
 * processes the queue. We surface the enqueued count and any error inline.
 */
function DriveSyncCard({ matterId }: { matterId: string }) {
  const [state, setState] = useState<'idle' | 'syncing' | 'done' | 'error'>('idle');
  const [count, setCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSync() {
    setState('syncing');
    setError(null);
    setCount(null);
    try {
      // enqueue_matter_drive_sync isn't in the generated database.types.ts yet
      // (added by migration 20260521000002). Cast around it, same pattern as
      // other recently-added DB objects in this codebase.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error: rpcErr } = await (supabase.rpc as any)('enqueue_matter_drive_sync', {
        matter_id: matterId,
      });
      if (rpcErr) throw rpcErr;
      setCount(typeof data === 'number' ? data : null);
      setState('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not queue the sync.');
      setState('error');
    }
  }

  return (
    <RailCard title="Drive sync">
      <div className="space-y-2 text-sm">
        <p className="text-xs text-slate-500">
          Mirror this matter's documents to the ADI shared drive. Only documents
          you have approved are copied across. Runs in the background, safe to
          leave.
        </p>
        <button
          type="button"
          onClick={handleSync}
          disabled={state === 'syncing'}
          className="btn-secondary inline-flex items-center gap-1.5 !py-1.5 !px-3 text-sm disabled:opacity-60"
        >
          <CloudUpload size={14} />
          {state === 'syncing' ? 'Queueing...' : 'Sync this matter to Drive'}
        </button>
        {state === 'done' && (
          <p className="text-xs text-green-700">
            {count === 0
              ? 'No documents to sync on this matter.'
              : `Queued ${count} document${count === 1 ? '' : 's'} for Drive sync.`}
          </p>
        )}
        {state === 'error' && error && (
          <p className="text-xs text-red-600">{error}</p>
        )}
      </div>
    </RailCard>
  );
}

function Fact({
  label,
  value,
  mono = false,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: 'good' | 'warn';
}) {
  const valueClass =
    tone === 'good'
      ? 'text-green-700'
      : tone === 'warn'
      ? 'text-amber-700'
      : 'text-[#0B2341]';
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={`text-sm font-semibold ${valueClass} ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}

/**
 * Client-facing wrapper around the unified timeline. Pulls the matter
 * context via the same hook the staff panel uses so labels and progression
 * always match.
 */
function ClientLifecycleCard({
  matterId,
  visaSubclass,
  variant,
  applicantContactId,
  sponsorContactId,
  matter,
}: {
  matterId: string;
  visaSubclass: string;
  variant: string | null;
  applicantContactId: string;
  sponsorContactId: string | null;
  matter: { status: string; initial_payment_date: string | null; final_payment_date: string | null };
}) {
  const { steps } = useMatterTimeline({
    matterId,
    visaSubclass,
    variant,
    applicantContactId,
    sponsorContactId,
    matter,
  });
  return (
    <div className="card p-4">
      <LifecycleTimeline steps={steps} mode="client" />
    </div>
  );
}

function PersonRow({
  label,
  person,
  edit,
}: {
  label: string;
  person: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
  edit?: React.ReactNode;
}) {
  if (!person) {
    return (
      <div className="py-1.5 text-sm">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-slate-500">{label}</div>
          {edit}
        </div>
        <div className="text-slate-400">Unassigned</div>
      </div>
    );
  }
  const name = [person.given_names, person.family_name].filter(Boolean).join(' ').trim() || person.email;
  return (
    <div className="py-1.5 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-slate-500">{label}</div>
        {edit}
      </div>
      <Link
        to={`/contacts/${person.id}`}
        className="font-medium text-[#0B2341] hover:text-[#2CABEA] hover:underline block"
      >
        {name}
      </Link>
      <a
        href={`mailto:${person.email}`}
        className="text-xs text-slate-500 hover:text-[#2CABEA] inline-flex items-center gap-1"
      >
        <Mail size={10} /> {person.email}
      </a>
    </div>
  );
}

/**
 * Build a human-readable description of an audit_log row.
 *
 * The audit triggers (fn_audit_changes) write `changes` as either:
 *   - INSERT/DELETE: the full row as JSONB
 *   - UPDATE: { before: {...}, after: {...} }
 * We derive a friendly title + detail per (action, table_name, diff).
 *
 * Falls back to a generic "<action> on <table>" if we don't recognise the
 * shape — better than nothing, but the well-known cases below are the ones
 * staff actually care about.
 */
function describeAudit(
  e: AuditEntry,
  contactNames: Record<string, string>,
  documentFilenames: Record<string, string> = {},
): { title: string; detail: string | null } {
  const c = (e.changes ?? {}) as Record<string, unknown>;
  const after = (c.after ?? null) as Record<string, unknown> | null;
  const before = (c.before ?? null) as Record<string, unknown> | null;
  const insertRow = e.action === 'insert' ? c : null;
  const nameFor = (id: unknown): string | null => {
    if (typeof id !== 'string' || !id) return null;
    return contactNames[id] ?? null;
  };
  // Document.* events (and trigger rows on the documents table) carry the
  // document id as audit_log.row_id. Resolve to filename via the lookup
  // populated by the audit fetch effect; falls back to null when the doc
  // has been deleted or wasn't preloaded.
  const filenameForRowId = (): string | null => {
    if (typeof e.row_id !== 'string' || !e.row_id) return null;
    return documentFilenames[e.row_id] ?? null;
  };

  // Custom logAuditEvent actions (dot-namespaced, e.g. 'document.upload').
  // These come from explicit logAuditEvent() calls in the SPA, not the
  // generic INSERT/UPDATE/DELETE trigger.
  if (e.action === 'document.upload') {
    const ch = (e.changes ?? {}) as Record<string, unknown>;
    const fname = (ch.original_filename as string | undefined)
      ?? (ch.filename as string | undefined)
      ?? filenameForRowId()
      ?? undefined;
    return { title: 'Document uploaded', detail: fname ?? null };
  }
  if (e.action === 'document.download') {
    return { title: 'Document downloaded', detail: filenameForRowId() };
  }
  if (e.action === 'document.review_saved') {
    const ch = (e.changes ?? {}) as Record<string, unknown>;
    const status = ch.review_status as string | undefined;
    const map: Record<string, string> = {
      approved: 'Document approved',
      needs_replacement: 'Document needs replacement',
      reviewing: 'Document marked reviewing',
      pending: 'Document marked pending',
    };
    return {
      title: status ? (map[status] ?? `Status: ${status}`) : 'Review saved',
      detail: filenameForRowId(),
    };
  }
  if (e.action === 'document.comment_posted') {
    const fname = filenameForRowId();
    return { title: 'Comment posted', detail: fname ? `on ${fname}` : null };
  }
  if (e.action === 'document.rotate_save') {
    return { title: 'Document rotated and saved', detail: filenameForRowId() };
  }
  if (e.action === 'document.approved') {
    return { title: 'Document approved', detail: filenameForRowId() };
  }
  if (e.action === 'document.needs_replacement') {
    return { title: 'Document needs replacement', detail: filenameForRowId() };
  }

  if (e.table_name === 'documents' && e.action === 'insert' && insertRow) {
    const filename = (insertRow.filename as string | undefined) ?? '';
    const checklist = (insertRow.checklist_item as string | undefined) ?? null;
    return {
      title: 'Document uploaded',
      detail: filename || checklist || null,
    };
  }
  if (e.table_name === 'documents' && e.action === 'update' && after) {
    const beforeStatus = before?.review_status as string | undefined;
    const afterStatus = after.review_status as string | undefined;
    const filename = (after.filename as string | undefined) ?? '';
    if (beforeStatus !== afterStatus && afterStatus) {
      const map: Record<string, string> = {
        approved: 'Document approved',
        needs_replacement: 'Document needs replacement',
        reviewing: 'Document under review',
        pending: 'Document marked pending',
      };
      return { title: map[afterStatus] ?? `Document status: ${afterStatus}`, detail: filename || null };
    }
    if (before?.deleted_at == null && after.deleted_at != null) {
      return { title: 'Document removed', detail: filename || null };
    }
    return { title: 'Document updated', detail: filename || null };
  }
  if (e.table_name === 'document_comments' && e.action === 'insert' && insertRow) {
    const body = (insertRow.body as string | undefined) ?? '';
    const trimmed = body.length > 80 ? body.slice(0, 80) + '…' : body;
    const authorName = nameFor(insertRow.author_contact_id);
    const docId = insertRow.document_id;
    const fname = typeof docId === 'string' ? documentFilenames[docId] ?? null : null;
    const onDoc = fname ? `on ${fname}` : null;
    const detail = [
      onDoc,
      authorName ? `from ${authorName}` : null,
      trimmed ? `"${trimmed}"` : null,
    ].filter(Boolean).join(' - ') || null;
    return { title: 'Comment posted', detail };
  }
  if (e.table_name === 'matters' && e.action === 'update' && after && before) {
    // Reassignments — checked first because they're the most specific.
    if (before.assigned_staff_id !== after.assigned_staff_id) {
      const newName = nameFor(after.assigned_staff_id);
      const oldName = nameFor(before.assigned_staff_id);
      if (newName) return { title: 'Reassigned', detail: oldName ? `from ${oldName} to ${newName}` : `to ${newName}` };
      return { title: 'Assignment cleared', detail: oldName ? `was ${oldName}` : null };
    }
    if (before.reviewer_staff_id !== after.reviewer_staff_id) {
      const newName = nameFor(after.reviewer_staff_id);
      if (newName) return { title: 'Reviewer set', detail: newName };
      return { title: 'Reviewer cleared', detail: null };
    }
    const beforeRel = before.relationship_status as string | null | undefined;
    const afterRel = after.relationship_status as string | null | undefined;
    if (beforeRel !== afterRel && afterRel) {
      return { title: 'Relationship status set', detail: `to ${afterRel}` };
    }
    const beforeStatus = before.status as string | undefined;
    const afterStatus = after.status as string | undefined;
    if (beforeStatus !== afterStatus && afterStatus) {
      return { title: 'Status changed', detail: `to ${afterStatus}` };
    }
    const beforeEng = (before.engagement_letter ?? null) as { status?: string } | null;
    const afterEng = (after.engagement_letter ?? null) as { status?: string } | null;
    if (beforeEng?.status !== afterEng?.status && afterEng?.status) {
      const map: Record<string, string> = {
        not_sent: 'Engagement letter reset to not sent',
        sent: 'Engagement letter marked sent',
        signed: 'Engagement letter marked signed',
        on_file: 'Engagement letter filed',
      };
      return { title: map[afterEng.status] ?? `Engagement letter: ${afterEng.status}`, detail: null };
    }
    const beforeFinal = before.final_payment_date as string | null | undefined;
    const afterFinal = after.final_payment_date as string | null | undefined;
    if (!beforeFinal && afterFinal) return { title: 'Final payment recorded', detail: null };
    const beforeInitial = before.initial_payment_date as string | null | undefined;
    const afterInitial = after.initial_payment_date as string | null | undefined;
    if (!beforeInitial && afterInitial) return { title: 'Initial payment recorded', detail: null };
    return { title: 'Matter updated', detail: null };
  }
  if (e.table_name === 'intake_forms' && e.action === 'update' && after && before) {
    const beforeStatus = before.status as string | undefined;
    const afterStatus = after.status as string | undefined;
    if (beforeStatus !== afterStatus && afterStatus) {
      const map: Record<string, string> = {
        completed: 'Intake form completed',
        submitted: 'Intake form submitted',
        in_progress: 'Intake form reopened',
        reopened: 'Intake form reopened',
      };
      return { title: map[afterStatus] ?? `Intake form status: ${afterStatus}`, detail: null };
    }
    // Touch updates (last_saved_at) — common, low-signal.
    return { title: 'Intake form saved', detail: null };
  }
  if (e.table_name === 'intake_forms' && e.action === 'insert' && insertRow) {
    const formCode = insertRow.form_code as string | undefined;
    return { title: 'Intake form started', detail: formCode ?? null };
  }
  // Fallback: terse but at least labelled.
  const verb = e.action === 'insert' ? 'Created' : e.action === 'update' ? 'Updated' : 'Removed';
  const obj = e.table_name?.replace(/_/g, ' ') ?? 'record';
  return { title: `${verb} ${obj}`, detail: null };
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Full timestamp for activity log: weekday, date, time, year. */
function formatFullDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
